/**
 * TrainingType List Page
 * List and manage Training Types
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import {
  ActionButton,
  PageShell,
  DataTable,
  type DataTableColumn,
  PrerequisiteBlock,
  type PrerequisiteBlockProps,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { trainingTypeService } from '../../services/trainingTypeService';
import { TrainingTypeData, TrainingTypeFilters, TrainingTypeStatus } from '../../types/trainingType';
import { getApiErrorMessage } from '../../types/common';
import { prerequisiteFromError } from '../../utils/prerequisiteFromError';
import { TrainingTypeFiltersComponent, TrainingTypeFiltersData } from './components/TrainingTypeFilters';
import { ImportDialog } from '../../components/common/ImportDialog';

/**
 * Format periodicity months to human-readable string
 */
function formatPeriodicity(months: number | null | undefined): string {
  if (months === null || months === undefined) return '-';
  if (months === 1) return 'Mensal';
  if (months === 3) return 'Trimestral';
  if (months === 6) return 'Semestral';
  if (months === 12) return 'Anual';
  if (months === 24) return '2 anos';
  if (months === 36) return '3 anos';
  if (months === 48) return '4 anos';
  if (months === 60) return '5 anos';
  if (months % 12 === 0) return `${months / 12} anos`;
  return `${months} meses`;
}

const columns: DataTableColumn<TrainingTypeData>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (tt) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {tt.name}
        </Typography>
        {tt.description && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
            {tt.description}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'legalBasis',
    label: 'Fundamentação Legal',
    render: (tt) => <Typography variant="body2">{tt.legalBasis || '-'}</Typography>,
  },
  {
    id: 'workload',
    label: 'Carga Horária',
    render: (tt) => <Typography variant="body2">{tt.workload ? `${tt.workload}h` : '-'}</Typography>,
  },
  {
    id: 'periodicity',
    label: 'Periodicidade',
    render: (tt) => <Typography variant="body2">{formatPeriodicity(tt.periodicityMonths)}</Typography>,
  },
  {
    id: 'esocialCode',
    label: 'Código eSocial',
    render: (tt) => (
      <Typography variant="body2" fontFamily="monospace">
        {tt.esocialCode || '-'}
      </Typography>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (tt) => (
      <Chip
        label={tt.status === TrainingTypeStatus.ACTIVE ? 'Ativo' : 'Inativo'}
        size="small"
        color={tt.status === TrainingTypeStatus.ACTIVE ? 'success' : 'default'}
      />
    ),
  },
];

export const TrainingTypeListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/training-types/:id/edit', pode('training-type.update'), 'Editar');
  const excluir = useAcao('training-type.delete', 'Excluir');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [trainingTypes, setTrainingTypes] = useState<TrainingTypeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<TrainingTypeFilters>({});
  const [bloqueio, setBloqueio] = useState<PrerequisiteBlockProps | null>(null);

  const loadTrainingTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (apiFilters.search) params.search = apiFilters.search;
      if (apiFilters.status) params.status = apiFilters.status;

      const response = await trainingTypeService.getAll(params);
      setTrainingTypes(response.trainingTypes);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao carregar tipos de treinamento');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadTrainingTypes();
  }, [loadTrainingTypes]);

  const handleFilter = useCallback((data: TrainingTypeFiltersData) => {
    const filters: TrainingTypeFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.status) filters.status = data.status as TrainingTypeFilters['status'];

    setApiFilters(filters);
    setPage(0);
  }, []);

  // Não há endpoint de dependências para tipo de treinamento: o bloqueio só aparece
  // depois da tentativa, traduzido do erro do servidor (HAS_LINKED_TRAININGS).
  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este tipo de treinamento?')) {
      return;
    }

    try {
      await trainingTypeService.delete(id);
      loadTrainingTypes();
    } catch (err: unknown) {
      const prerequisite = prerequisiteFromError(err);
      if (prerequisite) {
        setBloqueio(prerequisite);
        return;
      }
      setError(getApiErrorMessage(err, 'Erro ao excluir tipo de treinamento'));
    }
  };

  const handleEdit = (trainingType: TrainingTypeData) => {
    navigate(`/training-types/${trainingType.id}/edit`);
  };

  const handleExport = async () => {
    try {
      const blob = await trainingTypeService.exportToExcel(apiFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tipos-treinamento-export.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao exportar tipos de treinamento');
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Tipos de Treinamento' },
      ]}
      title="Gestão de Tipos de Treinamento"
      subtitle="Gerencie os tipos de treinamento disponíveis"
      actions={
        <>
          <ActionButton
            acao="training-type.read"
            rotulo="Exportar"
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Exportar
          </ActionButton>
          <ActionButton
            acao="training-type.import"
            rotulo="Importar"
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Importar
          </ActionButton>
          <RouteActionButton
            para="/training-types/new"
            exige={pode('training-type.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Tipo
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TrainingTypeFiltersComponent onFilter={handleFilter} loading={loading} total={total} />
      </Box>

      <DataTable
        columns={columns}
        rows={trainingTypes}
        getRowId={(tt) => tt.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum tipo de treinamento encontrado',
          description:
            'Os tipos definem carga horária e validade, e são o ponto de partida para registrar cada treinamento realizado.',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (tt) => handleDelete(tt.id),
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (n) => {
            setRowsPerPage(n);
            setPage(0);
          },
        }}
      />

      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={() => {
          setImportDialogOpen(false);
          loadTrainingTypes();
        }}
        title="Importar Tipos de Treinamento"
        description="Importe tipos de treinamento a partir de uma planilha Excel. Baixe o template para ver o formato esperado."
        templateFileName="template-tipos-treinamento.xlsx"
        onDownloadTemplate={() => trainingTypeService.downloadTemplate()}
        onImport={(file) => trainingTypeService.importFromExcel(file)}
        entityLabel="tipo(s) de treinamento"
      />

      <Dialog open={Boolean(bloqueio)} onClose={() => setBloqueio(null)}>
        <DialogTitle>Excluir tipo de treinamento</DialogTitle>
        <DialogContent>{bloqueio && <PrerequisiteBlock {...bloqueio} />}</DialogContent>
        <DialogActions>
          <Button onClick={() => setBloqueio(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default TrainingTypeListPage;
